import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import {COOKIE_CONSENT_STORAGE_KEY, getStoredCookieConsent} from "@/constants/cookieConsentStorage";
import styles from "./CookieConsentBadge.module.css";

const SCROLL_COLLAPSE_THRESHOLD = 60;

const CookieConsentBadge = ({onConsentStored}) => {
  const {t} = useTranslation();
  const [open, setOpen] = useState(() => getStoredCookieConsent() === null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > SCROLL_COLLAPSE_THRESHOLD) {
        setIsExpanded(false);
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, {passive: true});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const persist = choice => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, choice);
    } catch {}
    setOpen(false);
    onConsentStored?.(choice);
  };

  if (!open) {
    return null;
  }

  const collapsed = !isExpanded;

  const handleBadgeKeyDown = event => {
    if (!collapsed) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsExpanded(true);
    }
  };

  const collapsedBadgeProps = collapsed
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": t("cookieConsentMessage"),
        onClick: () => setIsExpanded(true),
        onKeyDown: handleBadgeKeyDown,
      }
    : {};

  return (
    <div
      className={`${styles.badge} ${collapsed ? styles.badgeCollapsed : ""}`}
      {...collapsedBadgeProps}
    >
      <span
        className={styles.icon}
        aria-hidden
      >
        🍪
      </span>
      <div className={`${styles.body} ${collapsed ? styles.bodyHidden : ""}`}>
        <p className={styles.message}>{t("cookieConsentMessage")}</p>
        <div className={styles.actions}>
          <SecondaryButton
            type="button"
            onClick={() => persist("rejected")}
          >
            {t("cookieConsentRejectBtn")}
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={() => persist("accepted")}
          >
            {t("cookieConsentAcceptBtn")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

CookieConsentBadge.propTypes = {
  onConsentStored: PropTypes.func,
};

export default CookieConsentBadge;
